import { APIError } from 'openai';
import { useNavigate } from 'react-router-dom';
import { X, AlertCircle, RefreshCw, Settings, MessageSquarePlus, Sparkles, Wrench, HelpCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useAISettings } from '@/hooks/useAISettings';
import { parseProviderModel } from '@/lib/parseProviderModel';
import { useAICredits } from '@/hooks/useAICredits';
import { CreditsDialog } from './CreditsDialog';
import { ProjectPreviewConsoleError } from '@/lib/consoleMessages';
import { useState } from 'react';
import { MalformedToolCallError } from '@/lib/errors/MalformedToolCallError';
import { EmptyMessageError } from '@/lib/errors/EmptyMessageError';

export interface AIErrorMessageProps {
  error: Error;
  onDismiss: () => void;
  onNewChat: () => void;
  onOpenModelSelector: () => void;
  onTryAgain?: () => void;
  onRequestConsoleErrorHelp?: (error: ProjectPreviewConsoleError) => void;
  onFixError?: (error: Error) => void;
  onExplainError?: (error: Error) => void;
  providerModel: string;
}

interface ErrorBody {
  message: string;
  actions?: Array<{
    label: string;
    icon?: React.ComponentType<{ className?: string }>;
    onClick: () => void;
  }>;
}

export function AIErrorMessage({ error, onDismiss, onNewChat, onOpenModelSelector, onTryAgain, onRequestConsoleErrorHelp, onFixError, onExplainError, providerModel }: AIErrorMessageProps) {
  const navigate = useNavigate();
  const { settings } = useAISettings();

  // Handle empty provider model gracefully
  let provider;
  try {
    provider = parseProviderModel(providerModel, settings.providers).provider;
  } catch {
    // If no valid provider model, use a default or undefined
    provider = undefined;
  }

  const credits = useAICredits(provider || { id: '', name: '', apiKey: '', nostr: false });
  const [showCreditsDialog, setShowCreditsDialog] = useState(false);

  const renderBody = (error: Error | APIError | MalformedToolCallError | ProjectPreviewConsoleError): ErrorBody => {
    // Handle Project Preview Console Errors
    if (error instanceof ProjectPreviewConsoleError) {
      return {
        message: 'Console errors detected in your project preview. Would you like help fixing them?',
        actions: [{
          label: 'Help fix errors',
          icon: Sparkles,
          onClick: () => {
            onRequestConsoleErrorHelp?.(error);
            onDismiss();
          },
        }],
      };
    }

    if (error instanceof MalformedToolCallError) {
      return {
        message: 'Received an incomplete response. This may be due to a network issue. Try again or switch models if the problem persists.',
        actions: [{
          label: 'Change model',
          icon: Settings,
          onClick: onOpenModelSelector,
        }],
      };
    }

    if (error instanceof EmptyMessageError) {
      return {
        message: 'The AI generated an empty response. Try again or switch to a different model.',
        actions: onTryAgain ? [{
          label: 'Try again',
          icon: RefreshCw,
          onClick: () => {
            onTryAgain();
            onDismiss();
          },
        }] : [{
          label: 'Change model',
          icon: Settings,
          onClick: onOpenModelSelector,
        }],
      };
    }

    // Handle OpenAI API errors with specific error codes
    if (error instanceof APIError) {
      switch (true) {
        case error.code === 'invalid_api_key':
        case error.code === 'invalid_request_error':
          return {
            message: 'Authentication issue. Please check your API key in settings.',
            actions: [{
              label: 'Check API key',
              icon: Settings,
              onClick: () => navigate('/settings/ai'),
            }],
          };

        case error.code === 'insufficient_quota': {
          if (credits.data) {
            return {
              message: `Your account has $${credits.data.amount.toFixed(2)} credits remaining. Add more to continue.`,
              actions: [{
                label: 'Add credits',
                onClick: () => setShowCreditsDialog(true),
              }],
            };
          } else {
            return {
              message: 'API key quota reached. Check your billing or try a different provider.',
              actions: [{
                label: 'Check settings',
                icon: Settings,
                onClick: () => navigate('/settings/ai'),
              }],
            };
          }
        }

        case error.code === 'rate_limit_exceeded':
          return {
            message: 'Rate limit reached. Please wait a moment before trying again.',
            actions: [],
          };

        case error.code === 'model_not_found':
          return {
            message: 'The selected model is not available. Please choose a different one.',
            actions: [{
              label: 'Choose model',
              icon: Settings,
              onClick: onOpenModelSelector,
            }],
          };

        case error.code === 'context_length_exceeded':
          return {
            message: 'Conversation is too long for this model. Start a new chat or try a model with a larger context window.',
            actions: [{
              label: 'New chat',
              icon: MessageSquarePlus,
              onClick: onNewChat,
            }, {
              label: 'Change model',
              icon: Settings,
              onClick: onOpenModelSelector,
            }],
          };

        case error.status === 403:
          return {
            message: 'API access denied. Your key may have expired or reached its limit.',
            actions: [{
              label: 'Check settings',
              icon: Settings,
              onClick: () => navigate('/settings/ai'),
            }, {
              label: 'Change model',
              onClick: onOpenModelSelector,
            }],
          };

        case error.status === 400:
          return {
            message: 'Request could not be processed. Try starting a new conversation or switching models.',
            actions: [{
              label: 'New chat',
              icon: MessageSquarePlus,
              onClick: onNewChat,
            }, {
              label: 'Change model',
              icon: Settings,
              onClick: onOpenModelSelector,
            }],
          };

        case error.status === 422:
          return {
            message: 'Request rejected by the AI provider. Try a different model.',
            actions: [{
              label: 'Choose model',
              icon: Settings,
              onClick: onOpenModelSelector,
            }],
          };

        case error.code === 'server_error':
        case error.code === 'service_unavailable':
          return {
            message: 'AI service is temporarily unavailable. Please try again in a moment.',
            actions: [],
          };

        default:
          return {
            message: error.message || 'An error occurred with the AI service.',
            actions: [],
          };
      }
    }

    // Default fallback
    return {
      message: error.message || 'Something went wrong. Please try again.',
      actions: [],
    };
  };

  const { message, actions = [] } = renderBody(error);

  // Check if Fix/Explain buttons should be shown (not for console errors which have their own handling)
  const showFixExplainButtons = !(error instanceof ProjectPreviewConsoleError) && (onFixError || onExplainError);

  return (
    <div className="py-3 px-4 bg-muted/50 border border-border/60 rounded-lg">
      <div className="flex items-start gap-3">
        <AlertCircle className="h-4 w-4 mt-0.5 flex-shrink-0 text-muted-foreground" />
        <div className="flex-1 min-w-0 space-y-2">
          <p className="text-sm text-muted-foreground leading-relaxed">
            {message}
          </p>
          <div className="flex flex-wrap gap-2">
            {/* Fix and Explain buttons */}
            {showFixExplainButtons && (
              <>
                {onFixError && (
                  <Button
                    variant="default"
                    size="sm"
                    onClick={() => {
                      onFixError(error);
                      onDismiss();
                    }}
                    className="h-7 text-xs"
                  >
                    <Wrench className="h-3 w-3 mr-1.5" />
                    Fix
                  </Button>
                )}
                {onExplainError && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      onExplainError(error);
                      onDismiss();
                    }}
                    className="h-7 text-xs"
                  >
                    <HelpCircle className="h-3 w-3 mr-1.5" />
                    Explain
                  </Button>
                )}
              </>
            )}
            {/* Contextual actions */}
            {actions.map((action) => {
              const Icon = action.icon;
              return (
                <Button
                  key={action.label}
                  variant="outline"
                  size="sm"
                  onClick={action.onClick}
                  className="h-7 text-xs"
                >
                  {Icon && <Icon className="h-3 w-3 mr-1.5" />}
                  {action.label}
                </Button>
              );
            })}
          </div>
        </div>
        <Button
          variant="ghost"
          size="sm"
          onClick={onDismiss}
          className="h-6 w-6 p-0 hover:bg-muted flex-shrink-0"
        >
          <X className="h-3.5 w-3.5" />
        </Button>
      </div>

      {provider && (
        <CreditsDialog
          open={showCreditsDialog}
          onOpenChange={setShowCreditsDialog}
          provider={provider}
        />
      )}
    </div>
  );
}
